import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useLoginUser } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/toast/CustomToast";
import {
  acknowledgeRosterChangeNotice,
  changeNoticeKeys,
  fetchRosterChangeNotice,
  type RosterChangeCell,
  type RosterChangeNotice,
} from "@/services/roster/changeNotices";
import "./roster-reissue.css";

export function RosterChangeTransition({
  cell,
  date = false,
  tone = "reissue",
}: {
  readonly cell: RosterChangeCell;
  readonly date?: boolean;
  readonly tone?: "reissue" | "version";
}) {
  const beforeClass =
    tone === "version"
      ? "text-muted-foreground"
      : "text-[var(--roster-reissue-before)]";
  const afterClass =
    tone === "version"
      ? "text-orange-600 dark:text-orange-400"
      : "text-[var(--roster-reissue)]";
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap">
      {date && (
        <span className="font-semibold">{Number(cell.date.slice(-2))}일</span>
      )}
      <del className={beforeClass}>{cell.before?.shift_id || "빈 칸"}</del>
      <span className="sr-only">에서</span>
      <span aria-hidden="true" className={beforeClass}>
        →
      </span>
      <span className={`font-bold ${afterClass}`}>
        {cell.after?.shift_id || "빈 칸"}
      </span>
      <span className="sr-only">으로 변경</span>
    </span>
  );
}
export function RosterReissueSummary({
  notice,
  isError,
  onRetry,
}: {
  readonly notice?: RosterChangeNotice;
  readonly isError: boolean;
  readonly onRetry: () => Promise<unknown>;
}) {
  const user = useLoginUser();
  if (isError)
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
      >
        재마감 변경 내용을 불러오지 못했습니다.
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void onRetry()}
        >
          다시 시도
        </button>
      </div>
    );
  if (!notice) return null;
  const myCells = notice.cells.filter(
    (cell) => cell.nurse_id === user?.nurse_id
  );
  return (
    <>
      <div
        className="flex min-w-0 flex-wrap items-center gap-2"
        data-testid="roster-reissue-summary"
      >
        <span className="rounded bg-[var(--roster-reissue)] px-2 py-0.5 text-[11px] font-bold text-white">
          재발행
        </span>
        <span className="text-xs font-bold text-[var(--roster-reissue)]">
          {notice.my.my_changed_cells > 0
            ? `내 근무 ${notice.my.my_changed_cells}건 변경`
            : "병동 근무표 변경"}
        </span>
        {myCells.slice(0, 2).map((cell) => (
          <span
            key={`${cell.nurse_id}-${cell.date}`}
            className="rounded-lg border border-[var(--roster-reissue-border)] bg-background px-2 py-1 text-[11px]"
          >
            <RosterChangeTransition cell={cell} date />
          </span>
        ))}
        {myCells.length > 2 && (
          <span className="text-xs text-muted-foreground">
            외 {myCells.length - 2}건
          </span>
        )}
      </div>
    </>
  );
}
export function RosterReissueDetailsButton({
  notice,
  nurseRoles,
  personal = false,
}: {
  readonly notice?: RosterChangeNotice;
  readonly nurseRoles?: ReadonlyMap<string, string>;
  readonly personal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!notice) return null;
  return (
    <>
      <button
        type="button"
        className="inline-flex h-7 items-center gap-2 rounded-full border border-[var(--roster-reissue)] bg-background px-2 text-xs font-semibold text-[var(--roster-reissue)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setOpen(true)}
      >
        {personal ? "변경 내용 확인" : `병동 전체 ${notice.total_cells}건`}{" "}
        <span aria-hidden="true">›</span>
      </button>
      {open && (
        <RosterChangeNoticeDialog
          key={notice.notice_id}
          notice={notice}
          nurseRoles={nurseRoles}
          personal={personal}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RosterChangeNoticeDialog({
  notice,
  onClose,
  nurseRoles,
  personal,
}: {
  readonly notice: RosterChangeNotice;
  readonly onClose: () => void;
  readonly nurseRoles?: ReadonlyMap<string, string>;
  readonly personal: boolean;
}) {
  const user = useLoginUser();
  const client = useQueryClient();
  const [id, setId] = useState(notice.notice_id);
  const [showTargets, setShowTargets] = useState(true);
  const scope = changeNoticeKeys.scope(
    user?.account_id ?? "",
    user?.office_id ?? ""
  );
  const detail = useQuery({
    queryKey: [...scope, "detail", id],
    queryFn: () => fetchRosterChangeNotice(id),
    initialData: id === notice.notice_id ? notice : undefined,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.is_manager && query.state.data.status === "open"
        ? 15_000
        : false,
  });
  const data = detail.data;
  const ack = useMutation({
    mutationFn: () => acknowledgeRosterChangeNotice(id),
    onSuccess: (result) => {
      toast.success(
        result.status === "closed" ? "모두 확인했습니다." : "확인했습니다."
      );
      void client.invalidateQueries({ queryKey: scope });
      onClose();
    },
  });
  const errorDetail = axios.isAxiosError(ack.error)
    ? ack.error.response?.data?.detail
    : undefined;
  const errorMessage =
    typeof errorDetail === "object" &&
    errorDetail &&
    typeof errorDetail.message === "string"
      ? errorDetail.message
      : "확인 처리에 실패했습니다. 다시 시도해 주세요.";
  const replacementId =
    typeof errorDetail === "object" &&
    errorDetail &&
    typeof errorDetail.superseded_by === "number"
      ? errorDetail.superseded_by
      : data?.superseded_by;
  const cells = personal
    ? (data?.cells ?? []).filter((cell) => cell.nurse_id === user?.nurse_id)
    : (data?.cells ?? []);
  const groups = new Map<string, { name: string; cells: RosterChangeCell[] }>();
  for (const cell of cells) {
    const group = groups.get(cell.nurse_id) ?? {
      name: cell.nurse_name || cell.nurse_id,
      cells: [],
    };
    group.cells.push(cell);
    groups.set(cell.nurse_id, group);
  }
  const ordered = [...groups].sort(
    ([a], [b]) => Number(b === user?.nurse_id) - Number(a === user?.nurse_id)
  );
  return (
    <Dialog
      open
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[85dvh] w-[calc(100vw-32px)] max-w-[640px] flex-col gap-4 overflow-hidden rounded-2xl p-6 font-pretendard"
        data-testid="roster-change-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-lg">
            변경 내역{" "}
            <span className="ml-1 inline-block rounded bg-[var(--roster-reissue)] px-1.5 py-0.5 align-middle text-[10px] font-bold text-white">
              재발행
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data?.year ?? notice.year}년 {data?.month ?? notice.month}월 근무표
            · 마지막으로 확인한 근무표와 비교
          </DialogDescription>
        </DialogHeader>
        {detail.isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>변경 내역을 불러오지 못했습니다.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void detail.refetch()}
            >
              다시 시도
            </Button>
          </div>
        ) : !data ? (
          <p role="status" className="text-sm">
            변경 내역을 불러오는 중입니다.
          </p>
        ) : (
          <>
            {data.status !== "open" && (
              <p role="status" className="text-sm text-muted-foreground">
                {data.status === "withdrawn"
                  ? "근무표가 마감 철회돼 지금은 확인할 수 없습니다."
                  : data.status === "superseded"
                    ? "그 사이 근무표가 다시 마감됐습니다."
                    : "확인 완료된 변경 내역입니다."}
              </p>
            )}
            <section
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              aria-label="근무표 변경 목록"
            >
              {ordered.length === 0 && (
                <p className="py-4 text-sm text-muted-foreground">
                  {personal
                    ? "내 근무 변경은 없습니다."
                    : "변경된 근무가 없습니다."}
                </p>
              )}
              {ordered.map(([nurseId, group]) => (
                <section
                  key={nurseId}
                  className="border-t border-border py-3 first:border-t-0 first:pt-0"
                >
                  <h3 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
                    <span className="min-w-0 break-words">{group.name}</span>
                    {nurseRoles?.get(nurseId) && (
                      <span className="shrink-0 text-xs font-normal text-muted-foreground">
                        {nurseRoles.get(nurseId)}
                      </span>
                    )}
                    {nurseId === user?.nurse_id && (
                      <span className="rounded bg-sig px-1 text-[10px] text-white">
                        나
                      </span>
                    )}
                    <span className="ml-auto text-xs text-[var(--roster-reissue)]">
                      {group.cells.length}건
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {group.cells.map((cell) => (
                      <div
                        key={cell.date}
                        className="flex flex-col gap-1 rounded-lg bg-light-muted px-2 py-2 text-[11px]"
                      >
                        <span>
                          {Number(cell.date.slice(-2))}일 (
                          {new Date(`${cell.date}T12:00:00`).toLocaleDateString(
                            "ko-KR",
                            { weekday: "short" }
                          )}
                          )
                        </span>
                        <RosterChangeTransition cell={cell} />
                        {cell.kind !== "cell" && (
                          <span className="text-muted-foreground">
                            {cell.kind === "added"
                              ? "새로 추가"
                              : "근무 전부 삭제"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              {data.is_manager && !personal && (
                <section className="mt-6" aria-label="근무표 변경 확인 현황">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold">
                      근무표 변경 확인 현황
                    </h3>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      확인 {data.confirm.acked_count}/
                      {data.confirm.target_count}명
                    </span>
                    {data.targets && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-auto size-7 rounded-full"
                        aria-label={
                          showTargets ? "명단 접기" : "근무자별 확인 여부"
                        }
                        aria-expanded={showTargets}
                        aria-controls="roster-change-targets"
                        onClick={() => setShowTargets((value) => !value)}
                      >
                        <ChevronDown
                          aria-hidden="true"
                          className={`size-4 transition-transform duration-200 motion-reduce:transition-none ${showTargets ? "rotate-180" : ""}`}
                        />
                      </Button>
                    )}
                  </div>
                  {showTargets && data.targets && (
                    <div id="roster-change-targets" className="mt-3 min-w-0">
                      <table
                        className="w-full table-fixed border-collapse text-xs"
                        aria-label="근무자별 변경 확인 여부"
                      >
                        <thead className="border-y border-border bg-light-muted text-muted-foreground">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2.5 text-left font-medium"
                            >
                              근무자
                            </th>
                            <th
                              scope="col"
                              className="w-12 px-2 py-2.5 text-center font-medium sm:w-16"
                            >
                              변경
                            </th>
                            <th
                              scope="col"
                              className="w-20 px-2 py-2.5 text-center font-medium sm:w-24"
                            >
                              확인 여부
                            </th>
                            <th
                              scope="col"
                              className="w-32 px-3 py-2.5 text-left font-medium sm:w-44"
                            >
                              확인 일시
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border border-b border-border">
                          {data.targets.map((target) => (
                            <tr key={target.nurse_id}>
                              <td className="break-words px-3 py-2.5 font-medium">
                                {target.nurse_name || target.nurse_id}
                                {nurseRoles?.get(target.nurse_id) && (
                                  <span className="ml-1.5 font-normal text-muted-foreground">
                                    {nurseRoles.get(target.nurse_id)}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2.5 text-center tabular-nums text-muted-foreground">
                                {target.my_changed_cells}건
                              </td>
                              <td
                                className={`whitespace-nowrap px-2 py-2.5 text-center ${target.active && target.acked_at ? "text-sig" : "text-muted-foreground"}`}
                              >
                                {!target.active
                                  ? "확인 대상 제외"
                                  : target.acked_at
                                    ? "확인 완료"
                                    : "미확인"}
                              </td>
                              <td className="px-3 py-2.5 text-[11px] tabular-nums text-muted-foreground sm:whitespace-nowrap">
                                {target.acked_at ? (
                                  <time dateTime={target.acked_at}>
                                    {new Date(target.acked_at).toLocaleString(
                                      "ko-KR",
                                      {
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}
                                    {target.ack_via
                                      ? ` · ${target.ack_via === "app" ? "앱" : "웹"}`
                                      : ""}
                                  </time>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
            </section>
          </>
        )}
        {ack.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
        {replacementId != null && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              ack.reset();
              setId(replacementId);
            }}
          >
            새 변경 내용 보기
          </Button>
        )}
        {data &&
          !detail.isError &&
          data.status === "open" &&
          (!data.is_manager || data.my.is_target || data.my.acked_at) && (
            <DialogFooter className="shrink-0 border-t border-border pt-3">
              {data.my.acked_at ? (
                <p className="text-xs text-muted-foreground">
                  확인함 · {new Date(data.my.acked_at).toLocaleString("ko-KR")}
                </p>
              ) : data.my.is_target ? (
                <Button
                  className="w-full bg-sig text-white hover:bg-sig/90"
                  disabled={ack.isPending || detail.isFetching}
                  onClick={() => ack.mutate()}
                >
                  {ack.isPending ? "확인 처리 중…" : "확인했습니다"}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  확인 대상이 아닙니다.
                </p>
              )}
            </DialogFooter>
          )}
      </DialogContent>
    </Dialog>
  );
}
